import React from 'react';
import { motion } from 'framer-motion';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-black py-24 px-6">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#121214] border border-white/10 rounded-2xl p-8 md:p-12"
        >
          <h1 className="text-4xl sm:text-5xl font-medium mb-6 tracking-tight">Privacy & Security Policy</h1>
          <div className="prose prose-invert max-w-none">
            <div className="space-y-6 text-[#A1A1A6] leading-relaxed">
              <p className="text-lg">
                At MyGaadiNumber, we take your privacy and security seriously. This Privacy & Security Policy outlines how we collect, use, protect, and share your personal information. By using our platform, you consent to the practices described in this policy.
              </p>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">Information We Collect</h2>
              <p>
                When you register on MyGaadiNumber, we collect the following information:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Personal Information:</strong> Name, email address, username, and password</li>
                <li><strong>Medical Information:</strong> Blood group for you and your family members (up to 3)</li>
                <li><strong>Contact Information:</strong> Alternative contact number (optional)</li>
                <li><strong>Vehicle Information:</strong> Vehicle names and registration numbers (up to 3 vehicles)</li>
                <li><strong>Photo Information:</strong> Profile photos for identification purposes</li>
                <li><strong>Payment Information:</strong> Transaction details for subscription payments</li>
                <li><strong>Referral Information:</strong> Referral codes and wallet transaction history</li>
              </ul>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">How We Use Your Information</h2>
              <p>
                The information we collect serves the following purposes:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Emergency Access:</strong> To provide blood group information to authenticated users during emergencies</li>
                <li><strong>Account Management:</strong> To create and maintain your user account</li>
                <li><strong>Service Delivery:</strong> To enable vehicle-based searches and profile management</li>
                <li><strong>Communication:</strong> To send important updates about your account and services</li>
                <li><strong>Payment Processing:</strong> To handle subscription payments and referral rewards</li>
                <li><strong>Platform Improvement:</strong> To analyze usage patterns and improve our services</li>
                <li><strong>Security:</strong> To detect and prevent fraud, abuse, and security threats</li>
              </ul>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">Information Sharing and Disclosure</h2>
              <p>
                Your blood group and personal information are shared only under specific circumstances:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Emergency Searches:</strong> When an authenticated user searches for a vehicle number, we display the associated blood group information, name, photo, alternative contact, and family member details. This is the core purpose of our platform.</li>
                <li><strong>Legal Requirements:</strong> We may disclose your information if required by law, court order, or government authority.</li>
                <li><strong>Service Providers:</strong> We work with trusted third-party service providers (payment gateways, hosting services) who are bound by confidentiality agreements.</li>
                <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred to the new entity.</li>
              </ul>
              <p>
                <strong>Important:</strong> We do NOT sell, rent, or trade your personal information to third parties for marketing purposes.
              </p>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">Emergency Sharing Consent</h2>
              <p>
                By registering on MyGaadiNumber, you explicitly consent to the sharing of your blood group information, name, photo, alternative contact number, vehicle details, and family members' blood group information with other authenticated users of the platform during emergency searches.
              </p>
              <p>
                This consent is fundamental to the operation of our emergency assistance platform. If you do not wish to share this information, please do not register on MyGaadiNumber. You may withdraw your consent by deleting your account, though this will remove all your information from our system.
              </p>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">Data Security Measures</h2>
              <p>
                We employ industry-standard security measures to protect your information:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Encryption:</strong> All passwords are encrypted using bcrypt hashing algorithm</li>
                <li><strong>Secure Authentication:</strong> JWT-based token authentication with httpOnly cookies</li>
                <li><strong>Session Management:</strong> Automatic session expiration and refresh token rotation</li>
                <li><strong>Access Control:</strong> Only authenticated users can access emergency information</li>
                <li><strong>Brute Force Protection:</strong> Account lockout after multiple failed login attempts</li>
                <li><strong>Database Security:</strong> Secure MongoDB database with encrypted connections</li>
                <li><strong>Regular Updates:</strong> Continuous security patches and system updates</li>
              </ul>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">User Rights and Control</h2>
              <p>
                You have the following rights regarding your personal information:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Access:</strong> You can access your complete profile information through your dashboard at any time</li>
                <li><strong>Modification:</strong> You can update your personal details, blood group, vehicle information, and family member details without additional charges</li>
                <li><strong>Deletion:</strong> You can request account deletion by contacting us (Note: This is permanent and cannot be reversed)</li>
                <li><strong>Correction:</strong> You can correct any inaccurate information in your profile</li>
                <li><strong>Data Portability:</strong> You can request a copy of your data in a structured format</li>
              </ul>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">Data Retention</h2>
              <p>
                We retain your information for as long as your account remains active. If you choose to delete your account, we will remove your personal information from our active database within 30 days. However, we may retain certain information for legal compliance, fraud prevention, and audit purposes for up to 7 years as required by law.
              </p>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">Photo Storage and Privacy</h2>
              <p>
                Photos uploaded to MyGaadiNumber are stored securely and are only displayed during emergency searches. We use base64 encoding for photo storage. These photos are meant for identification purposes during emergencies and are subject to the same sharing consent as other personal information.
              </p>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">Third-Party Services</h2>
              <p>
                Our platform integrates with third-party services for specific functionalities:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Payment Gateway:</strong> For processing subscription payments (coming soon)</li>
                <li><strong>Cloud Hosting:</strong> For reliable and secure platform hosting</li>
                <li><strong>Email Services:</strong> For sending important account-related communications</li>
              </ul>
              <p>
                These third-party services have their own privacy policies, and we recommend reviewing them. We only share the minimum information necessary for these services to function.
              </p>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">Cookies and Tracking</h2>
              <p>
                We use httpOnly cookies for authentication purposes. These cookies are essential for the platform to function and cannot be disabled. We do not use tracking cookies or third-party advertising cookies. Our cookies are session-based and expire after 7 days for security purposes.
              </p>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">Children's Privacy</h2>
              <p>
                MyGaadiNumber is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from minors. If you are under 18, please do not register or provide any information on our platform. Parents or guardians may register on behalf of minors and include them as family members.
              </p>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">Data Accuracy and User Responsibility</h2>
              <p>
                While we provide the platform for storing and sharing blood group information, we rely on users to provide accurate information. MyGaadiNumber is not responsible for any consequences arising from incorrect or false information provided by users. It is your responsibility to ensure that all information you provide, especially blood group details, is accurate and up-to-date.
              </p>
              <p>
                We strongly encourage users to verify and update their information regularly, especially after any changes in medical status or vehicle ownership.
              </p>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">Reporting Abuse and Spam</h2>
              <p>
                If you notice any misuse of the platform, spam, or suspicious activity, please contact us immediately at +91 9014556536. We take all reports seriously and will investigate promptly. Users found to be abusing the platform or providing false information may have their accounts suspended or terminated.
              </p>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">Breach Notification</h2>
              <p>
                In the unlikely event of a data breach that compromises your personal information, we will notify you within 72 hours via email. We will also inform relevant authorities as required by law and take immediate steps to secure the system and prevent further breaches.
              </p>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">International Users</h2>
              <p>
                MyGaadiNumber is primarily designed for users in India. If you access our platform from outside India, please note that your information will be transferred to and processed in India. By using our platform, you consent to this transfer and processing.
              </p>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">Changes to This Policy</h2>
              <p>
                We may update this Privacy & Security Policy from time to time to reflect changes in our practices, legal requirements, or platform features. We will notify you of any significant changes via email or through a prominent notice on our platform. Your continued use of MyGaadiNumber after such changes constitutes acceptance of the updated policy.
              </p>

              <h2 className="text-2xl font-medium text-white mt-8 mb-4">Contact Information</h2>
              <p>
                If you have any questions, concerns, or requests regarding this Privacy & Security Policy or your personal information, please contact us:
              </p>
              <p className="text-white">
                <strong>Phone:</strong> +91 9014556536<br />
                <strong>Address:</strong> MyGaadiNumber, Door No. 302, Chaimina Enclave, AS Raju Nagar, Kukatpally, Hyderabad, Telangana - 500072, India
              </p>

              <p className="text-white font-medium mt-8">
                Last Updated: January 2026
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Privacy;